class NLTestimonialCarousel extends HTMLElement {
  connectedCallback() {
    if (this.eventController) return;

    this.eventController = new AbortController();
    const { signal } = this.eventController;
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 5000;
    this.activeIndex = 0;
    this.scrollFrame = null;
    this.autoplayTimer = null;
    this.pointerPaused = false;
    this.focusPaused = false;
    this.interactionPaused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.track || this.slides.length < 2) return;

    this.onScroll = this.onScroll.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true, signal });
    this.track.addEventListener('pointerdown', () => {
      this.interactionPaused = true;
      this.stopAutoplay();
    }, { signal });
    this.track.addEventListener('pointerup', () => {
      this.interactionPaused = false;
      this.startAutoplay();
    }, { signal });
    this.track.addEventListener('pointercancel', () => {
      this.interactionPaused = false;
      this.startAutoplay();
    }, { signal });
    this.previousButton?.addEventListener('click', () => this.select(this.activeIndex - 1), { signal });
    this.nextButton?.addEventListener('click', () => this.select(this.activeIndex + 1), { signal });
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.select(index), { signal }));
    this.addEventListener('mouseenter', () => {
      this.pointerPaused = true;
      this.stopAutoplay();
    }, { signal });
    this.addEventListener('mouseleave', () => {
      this.pointerPaused = false;
      this.startAutoplay();
    }, { signal });
    this.addEventListener('focusin', () => {
      this.focusPaused = true;
      this.stopAutoplay();
    }, { signal });
    this.addEventListener('focusout', () => {
      this.focusOutTimer = window.setTimeout(() => {
        this.focusPaused = this.contains(document.activeElement);
        this.startAutoplay();
      }, 0);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.select(this.activeIndex - 1);
      if (event.key === 'ArrowRight') this.select(this.activeIndex + 1);
    }, { signal });
    document.addEventListener('visibilitychange', this.onVisibilityChange, { signal });
    this.update();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    if (this.focusOutTimer) window.clearTimeout(this.focusOutTimer);
    this.eventController?.abort();
    this.eventController = null;
  }

  select(index) {
    const normalizedIndex = (index + this.slides.length) % this.slides.length;
    const slide = this.slides[normalizedIndex];
    this.activeIndex = normalizedIndex;
    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
    });
    this.update();
    this.restartAutoplay();
  }

  onScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const trackWidth = this.track.clientWidth || 1;
      this.activeIndex = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / trackWidth)));
      this.update();
    });
  }

  update() {
    this.slides.forEach((slide, index) => {
      if (index === this.activeIndex) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.activeIndex);
      if (index === this.activeIndex) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  startAutoplay() {
    if (this.reducedMotion.matches || document.hidden || this.pointerPaused || this.focusPaused || this.interactionPaused || this.autoplayTimer) return;
    this.autoplayTimer = window.setInterval(() => this.select(this.activeIndex + 1), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  onVisibilityChange() {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  }
}

if (!customElements.get('nl-testimonial-carousel')) {
  customElements.define('nl-testimonial-carousel', NLTestimonialCarousel);
}
